import React, { useEffect, useRef, useState } from 'react';

import { ScrollStopper } from '@gumtree/ui-library';
import { Slide } from '@gumtree/ui-library/src/transitions';
import { trackGA4Event, trackGA4PreNav } from '@gumtree/shared/src/util/ga4-shared';
import { setCategoryBrpEntryCookie } from '@gumtree/shared/src/util/category-brp-entry-cookie';

import type { MhcTreeItem } from './mhc-category-data';
import { buildMhcNavigationClickEvent } from './tracking';
import {
    CategoryRow,
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerOverlay,
    DrawerTitle,
    RowChevron,
    SubcategoryDrawer,
    SubcategoryLink,
    TreeIcon,
} from './mhc-mobile-category-nav.style';
import CloseIcon from '../images/assets/close.svg';
import BackArrowIcon from '../images/assets/back-arrow.svg';
import ChevronForwardIcon from '../images/assets/chevron-forward.svg';

interface Props {
    fullScreen: boolean;
    isOpen: boolean;
    onClose: (syncHistory?: boolean) => void;
    treeItems: MhcTreeItem[];
}

const drawerTransitionDurationMs = 300;

const MhcCategoryTreeDrawer = ({ fullScreen, isOpen, onClose, treeItems }: Props) => {
    const [activeTreeItem, setActiveTreeItem] = useState<MhcTreeItem | null>(null);
    const [isDrawerChromeVisible, setIsDrawerChromeVisible] = useState(isOpen);
    const drawerRootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) {
            setIsDrawerChromeVisible(true);
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            setIsDrawerChromeVisible(false);
        }, drawerTransitionDurationMs);

        return () => window.clearTimeout(timeoutId);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setActiveTreeItem(null);
        }
    }, [isOpen]);

    const renderTreeContent = () => (
        <DrawerContent data-testid="mhc-category-tree-content">
            <DrawerHeader>
                {fullScreen && <BackArrowIcon onClick={() => onClose(true)} />}
                <DrawerTitle>All Categories</DrawerTitle>
                {!fullScreen && <CloseIcon onClick={() => onClose(true)} />}
            </DrawerHeader>
            {treeItems.map((item) => {
                const isExpanded = activeTreeItem?.seoName === item.seoName;
                const panelId = `mhc-subcategory-panel-${item.seoName}`;
                const toggleTreeItem = () => {
                    if (!isExpanded) {
                        trackGA4Event<GA4.NavigationMenuExpanded>({
                            event: 'navigation_menu_expanded',
                            navigationCategory: item.label,
                        });
                    }
                    setActiveTreeItem(isExpanded ? null : item);
                };

                return (
                    <React.Fragment key={`mhc-l1-${item.seoName}`}>
                        <CategoryRow
                            aria-controls={panelId}
                            aria-expanded={isExpanded}
                            expanded={isExpanded}
                            onClick={toggleTreeItem}
                            type="button"
                        >
                            {item.iconSrc && (
                                <TreeIcon
                                    data-testid={`mhc-tree-icon-${item.seoName}`}
                                    src={item.iconSrc}
                                    alt=""
                                />
                            )}
                            {item.label}
                            <RowChevron expanded={isExpanded}>
                                <ChevronForwardIcon />
                            </RowChevron>
                        </CategoryRow>
                        {isExpanded && (
                            <SubcategoryDrawer
                                data-testid={`mhc-subcategory-drawer-${item.seoName}`}
                                id={panelId}
                            >
                                {item.children.map((child) => (
                                    <SubcategoryLink
                                        href={child.path || '#'}
                                        key={`mhc-l2-${item.seoName}-${child.seoName}`}
                                        onClick={() => {
                                            if (child.brpEntry) {
                                                setCategoryBrpEntryCookie(child.brpEntry);
                                            }
                                            trackGA4PreNav(buildMhcNavigationClickEvent(child));
                                        }}
                                    >
                                        {child.label}
                                    </SubcategoryLink>
                                ))}
                            </SubcategoryDrawer>
                        )}
                    </React.Fragment>
                );
            })}
        </DrawerContent>
    );

    return (
        <div ref={drawerRootRef}>
            {isDrawerChromeVisible && (
                <ScrollStopper dialogRoot={drawerRootRef} useEventBasedScrollLock />
            )}
            {isDrawerChromeVisible && (
                <DrawerOverlay
                    className="overlay"
                    aria-label="Close categories"
                    onClick={() => onClose(true)}
                    type="button"
                />
            )}
            <Slide in={isOpen} direction="left">
                <Drawer
                    aria-label="All Categories"
                    className="dialog"
                    fullScreen={fullScreen}
                    role="dialog"
                >
                    {renderTreeContent()}
                </Drawer>
            </Slide>
        </div>
    );
};

export default MhcCategoryTreeDrawer;
